import { SOCKET_DOMAIN } from "constants/schemas";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType>({ socket: null });

export const SocketProvider = ({ children }: { children: ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const newSocket = io(SOCKET_DOMAIN, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("🔗 Socket connected", newSocket.id);
    });

    newSocket.on("connect_error", (err) => {
      console.error("❌ Socket error:", err);
      const attempts = newSocket.io.reconnectionAttempts();
      const maxAttempts = newSocket.io.opts.reconnectionAttempts || 5;
      if (attempts >= maxAttempts) {
        console.log("Max reconnection attempts reached, disconnecting...");
        newSocket.disconnect();
      }
    });

    newSocket.on("error", (err) => {
      console.error("Socket general error:", err);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      if (reason === "io server disconnect") {
        newSocket.connect();
      }
    });

    newSocket.io.on("reconnect_attempt", (attempt) => {
      console.log(`Reconnection attempt ${attempt}`);
    });

    newSocket.io.on("reconnect_failed", () => {
      console.log("Failed to reconnect");
    });

    return () => {
      console.log("🔌 Socket disconnected");
      newSocket.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);